import React, { type ReactNode } from 'react';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	createContainer,
	createHook,
	createStore,
	createStateHook,
} from '@atlassian/react-sweet-state';
import type { AppProps, TicketTypeData } from '../common/types.tsx';
import createTicketType from './actions/create-ticket-type/index.tsx';
import deleteTicketType from './actions/delete-ticket-type/index.tsx';
import resetChangeWorkflow from './actions/reset-change-workflow/index.tsx';
import setChangeWorkflowToEdit from './actions/set-change-workflow-to-edit/index.tsx';
import setIssueTypeWorkflow from './actions/set-issue-type-workflow/index.tsx';
import setProjectWorkflowScheme from './actions/set-project-workflow-scheme/index.tsx';
import setRequestTypeCategory from './actions/set-request-type-category/index.tsx';
import setRequestTypeFormData from './actions/set-request-type-form-data/index.tsx';
import setRequestTypeGroups from './actions/set-request-type-groups/index.tsx';
import setRequestTypeIssueType from './actions/set-request-type-issuetype/index.tsx';
import setRequestTypeSecurity from './actions/set-request-type-security/index.tsx';
import setRequestTypesPractice from './actions/set-request-types-practice/index.tsx';
import setTicketTypeIcon from './actions/set-ticket-type-icon/index.tsx';
import setTicketTypeName from './actions/set-ticket-type-name/index.tsx';
import setTicketTypes from './actions/set-ticket-types/index.tsx';
import {
	appPropsSelector,
	changeWorkflowSelector,
	collatedTicketTypesSelector,
	collatedUnmappedRequestTypesSelector,
	pendingTicketTypeCreationSelector,
	pendingTicketTypeDeletionSelector,
	requestTypeFormData,
	ticketTypesByPracticeSelector,
	ticketTypeSelector,
	ticketTypeSetIconErrorSelector,
	ticketTypeSetNameErrorSelector,
	unmappedRequestTypesSelector,
	projectWorkflowSchemeSelector,
} from './selectors/index.tsx';

const actions = {
	setTicketTypes,
	deleteTicketType,
	setRequestTypeGroups,
	setRequestTypeIssueType,
	setRequestTypesPractice,
	setTicketTypeIcon,
	setTicketTypeName,
	createTicketType,
	setChangeWorkflowToEdit,
	resetChangeWorkflow,
	setRequestTypeFormData,
	setProjectWorkflowScheme,
	setIssueTypeWorkflow,
	setRequestTypeCategory,
	setRequestTypeSecurity,
} as const;

const initialState: TicketTypeData = {
	ticketTypes: {},
	appProps: {
		projectKey: '',
		projectId: -1,
	},
	pendingTicketTypeCreation: {
		error: null,
		data: null,
	},
	pendingTicketTypeDeletion: {
		error: null,
		data: null,
	},
	changeWorkflow: {
		isShowing: false,
		requestTypeId: null,
	},
	requestTypeFormData: {
		data: null,
		error: null,
	},
	projectWorkflowScheme: {
		loading: true,
		error: null,
		issueTypeIdToWorkflow: {},
		defaultWorkflow: null,
	},
};

const Store = createStore({ initialState, actions, name: 'ticket-type-store' });

export const useTicketTypeStore = createHook(Store, {
	selector: ticketTypeSelector,
});

export const useChangeWorkflowStore = createHook(Store, {
	selector: changeWorkflowSelector,
});

export const useTicketTypesStore = createHook(Store, {
	selector: ticketTypesByPracticeSelector,
});

export const useUnmappedRequestTypes = createHook(Store, {
	selector: unmappedRequestTypesSelector,
});

export const useCollatedUnmappedRequestTypes = createHook(Store, {
	selector: collatedUnmappedRequestTypesSelector,
});

export const useCollatedTicketTypes = createHook(Store, { selector: collatedTicketTypesSelector });

export const useTicketTypePendingCreationStore = createHook(Store, {
	selector: pendingTicketTypeCreationSelector,
});

export const useTicketTypeSetNameError = createHook(Store, {
	selector: ticketTypeSetNameErrorSelector,
});

export const useTicketTypeSetIconError = createHook(Store, {
	selector: ticketTypeSetIconErrorSelector,
});

export const usePendingTicketTypeDeletionStore = createHook(Store, {
	selector: pendingTicketTypeDeletionSelector,
});

export const useAppPropsStore = createHook(Store, {
	selector: appPropsSelector,
});

export const useTicketTypeStoreActions = createHook(Store, {
	selector: null,
});

export const useRequestTypeFormData = createStateHook(Store, {
	selector: requestTypeFormData,
});

export const useProjectWorkflowScheme = createHook(Store, {
	selector: projectWorkflowSchemeSelector,
});

const Container = createContainer<TicketTypeData, typeof actions, AppProps>(Store, {
	onInit:
		() =>
		({ setState }, { projectKey, projectId }) => {
			setState({ appProps: { projectKey, projectId } });
		},
});

export const TicketTypeContainer = ({
	children,
	projectKey,
	projectId,
}: {
	children: ReactNode;
} & AppProps) => (
	<Container scope={projectKey} {...{ projectKey, projectId }}>
		{children}
	</Container>
);
